// Home — hero + narrative only. Showcase / What's New / Capabilities / Pricing
// live on their own routes (§4.2 revised: real page navigation, no side rail).
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { ArrowRight, Search, LineChart } from "lucide-react";
import { C, SectionLabel, PublicPage } from "@/components/PublicShell";
import { AssistantShowcase } from "@/components/AssistantShowcase";

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const cta = () => navigate("/sign-up");

  return (
    <PublicPage>
      {/* ── HERO ── */}
      <section className="pt-16 pb-20 px-5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight mb-5" style={{ color: C.text }}>
              Probabilistic analytics<br />
              <span style={{ color: C.muted }}>for self-directed investors.</span>
            </h1>
            <p className="text-sm leading-relaxed mb-6 max-w-lg" style={{ color: C.muted }}>
              MarkovLab helps self-directed investors generate options income and
              oversee a portfolio with the same platform — Wheel, CSP, covered calls,
              Scanner, Position Management, Trade Log, and Backtest Lab — not a generic
              price dashboard. Analytics, never advice.
              A built-in Portfolio Assistant (beta) answers questions about your own portfolio&apos;s
              computed risk, screens, and allocations in plain language — readouts of your own
              analytics, never advice.
            </p>
            <div className="flex flex-wrap items-center gap-2 mb-6 text-[11px]"
              data-testid="hero-tier-strip">
              <span className="px-2.5 py-1 rounded-full font-semibold"
                style={{ background: "rgba(255,255,255,0.06)", color: C.text, border: `1px solid ${C.border}` }}>
                Demo · 15 days free
              </span>
              <span className="px-2.5 py-1 rounded-full font-semibold"
                style={{ background: "rgba(0,200,150,0.12)", color: C.accent, border: `1px solid ${C.accent}` }}>
                MarkovLab · priced at checkout
              </span>
              <span className="px-2.5 py-1 rounded-full font-semibold"
                style={{ background: "rgba(79,195,247,0.10)", color: C.cyan, border: `1px solid ${C.cyan}` }}>
                Pro · In development
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={cta}
                className="h-10 px-6 text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-opacity hover:opacity-85"
                style={{ background: C.accent, color: "#0a0a0a" }}
                data-testid="hero-cta">
                Start 15-day Demo <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <Link to="/platform"
                className="h-10 px-6 text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors hover:bg-white/5"
                style={{ background: "transparent", color: C.text, border: `1px solid ${C.borderHover}` }}
                data-testid="hero-demo-link">
                <LineChart className="w-3.5 h-3.5" style={{ color: C.accent }} /> Take a look
              </Link>
            </div>
            <p className="mt-3 text-xs" style={{ color: "#555" }}>Free 15-day demo — no credit card required. Auto-pauses on day 16. Broker connections are included on every account.</p>
            {/* Iter176 — jurisdiction-scope + options risk disclaimer directly
                below the primary CTA (per remediation §A.2). Required to
                address FCA financial-promotion, HK SFC "targeting the public",
                and CMVM CVM Art. 295 exposure. */}
            <p className="mt-4 text-[10px] leading-relaxed max-w-lg" data-testid="hero-jurisdiction-disclaimer"
              style={{ color: "#555", borderTop: `1px dashed ${C.border}`, paddingTop: "10px" }}>
              MarkovLab provides analytics and decision-support software for self-directed investors. MarkovLab does not provide investment, tax, legal, or trading advice and is not a broker-dealer, investment adviser, or custodian.
              Tickers and symbols referenced throughout the platform are used for illustrative purposes only and do not constitute a recommendation to buy, sell, or hold any security.
              MarkovLab&apos;s website, software, and any associated services are not intended for persons in any jurisdiction where MarkovLab is not authorized to do business, or where the analytics, tools, or other services offered by MarkovLab would be contrary to the securities regulations, derivatives regulations, or other local laws and regulations of that jurisdiction &mdash; including, without limitation, the United Kingdom (Financial Conduct Authority), Hong Kong (Securities and Futures Commission), and Portugal (Comiss&atilde;o do Mercado de Valores Mobili&aacute;rios).
              Options involve risk and are not suitable for all investors, as the special risks inherent to options trading may expose investors to potentially significant losses.
              Please read{" "}
              <a href="https://www.theocc.com/company-information/documents-and-archives/publications" target="_blank" rel="noopener noreferrer"
                style={{ color: C.muted, textDecoration: "underline" }}>Characteristics and Risks of Standardized Options</a>
              {" "}before trading options.
            </p>
          </div>

          {/* Mock Scanner Row */}
          <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${C.border}`, background: C.card }}>
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ borderBottom: `1px solid ${C.border}`, background: C.cardAlt }}>
              <Search className="w-3 h-3" style={{ color: C.muted }} />
              <span className="text-xs font-medium" style={{ color: C.muted }}>Scanner</span>
            </div>
            <div className="px-4 py-2" style={{ borderBottom: `1px solid ${C.border}` }}>
              <div className="grid grid-cols-5 text-[10px] font-medium" style={{ color: "#555" }}>
                <span>Ticker</span><span>Regime</span><span>Prob</span><span>Regime-Fit Strategy (Score)</span><span className="text-right">Score</span>
              </div>
            </div>
            {[
              { t: "SPY", regime: "Bull", prob: "100%", strat: "Bull Put Spread", score: 85, rc: C.accent },
              { t: "AAPL", regime: "Quiet", prob: "94%", strat: "Cash Secured Put", score: 78, rc: C.cyan },
              { t: "NVDA", regime: "Vol", prob: "71%", strat: "Iron Condor", score: 62, rc: C.amber },
              { t: "BA", regime: "Bear", prob: "88%", strat: "Protective Put", score: 41, rc: C.red },
            ].map((r, i) => (
              <div key={r.t} className="grid grid-cols-5 items-center px-4 py-2 text-xs" style={{ borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
                <span className="font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace", color: C.text }}>{r.t}</span>
                <span className="font-medium" style={{ color: r.rc, fontFamily: "'JetBrains Mono', monospace", fontSize: "10px" }}>{r.regime}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.muted, fontSize: "10px" }}>{r.prob}</span>
                <span style={{ color: C.muted, fontSize: "11px" }}>{r.strat}</span>
                <span className="text-right font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace", color: r.score >= 70 ? C.accent : r.score >= 50 ? C.amber : C.red }}>{r.score}</span>
              </div>
            ))}
            {/* Iter176 — CMVM/FCA required disclaimer adjacent to
                logged-out sample scanner output (per remediation §B.2) */}
            <div className="px-4 py-2.5 text-[10px] italic" style={{ color: C.muted, borderTop: `1px solid ${C.border}`, background: C.cardAlt }}>
              Illustrative model output for informational purposes only — not a recommendation to buy or sell any security.
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-20 px-5"
        style={{ background: "#0d0d0d", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-5xl mx-auto">
          <SectionLabel text="The problem" color={C.muted} />
          <h2 className="text-base sm:text-lg font-bold mb-10" style={{ color: C.muted }}>
            Position analysis has a lifecycle problem.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              "Investors spend 90% of their time on entry decisions and almost none on position monitoring. Entries are structured. Everything after is improvised.",
              "P&L colour drives decisions instead of probability. Green means hold. Red means panic. Neither is a framework.",
              "Regime changes mid-trade and no one notices until the damage is done. The strategy that made sense at entry no longer fits the market.",
            ].map((text, i) => (
              <div key={i}>
                <span className="text-xs font-bold" style={{ color: C.accent, fontFamily: "'JetBrains Mono', monospace" }}>0{i + 1}</span>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.muted }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 px-5">
        <div className="max-w-5xl mx-auto">
          <SectionLabel text="How it works" color={C.muted} />
          <h2 className="text-base sm:text-lg font-bold mb-12" style={{ color: C.muted }}>
            One platform. The full trade lifecycle.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { step: "Enter with context", body: "The Scanner classifies regime for every ticker on your watchlist. TickerWorkbench ranks all strategies by regime suitability and validates each against 24 months of backtest data. See which setups meet your criteria before you decide." },
              { step: "Monitor with rules", body: "The Reliability Lab tracks every open position against its entry regime. See regime drift, health scores, and exit signals in real time. Know when conditions have changed before your P&L tells you." },
              { step: "Act on your own rules", body: "Rules-based alerts track the pre-defined thresholds you configure. Get notified when your regime-flip, sojourn/probability, or drawdown-floor thresholds are met — you decide what to do next." },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold"
                    style={{ border: `1px solid ${C.border}`, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{i + 1}</span>
                  <span className="text-sm font-bold" style={{ color: C.text }}>{item.step}</span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: C.muted }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PORTFOLIO ASSISTANT (BETA) ── */}
      <section className="py-20 px-5" data-testid="landing-assistant-section">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <SectionLabel text="Portfolio Assistant (beta)" color={C.muted} />
            <h2 className="text-base sm:text-lg font-bold mb-4" style={{ color: C.text }}>
              Ask your own analytics. In plain language.
            </h2>
            <p className="text-sm leading-relaxed mb-4" style={{ color: C.muted }}>
              The Portfolio Assistant produces natural-language readouts of what MarkovLab&apos;s
              engines and your own configured rules already compute — downside protection,
              risk-limit breaches, quotes, IV rank, option-chain figures, and concept explanations
              from the docs. Every number comes from the same engines that power the dashboards.
            </p>
            <ul className="text-xs leading-relaxed space-y-1.5 mb-4" style={{ color: C.muted }}>
              {["Closed set of supported topics — it reads out, it never opines",
                "Every response carries its data sources and the standard disclaimer",
                "Asked for a recommendation? It declines — the decision stays yours"].map(t => (
                <li key={t} className="flex gap-2"><span style={{ color: C.accent }}>—</span><span>{t}</span></li>
              ))}
            </ul>
            <p className="text-[10px]" style={{ color: "#555" }}>
              Informational and educational only. The Assistant does not provide recommendations or advice,
              and never places or routes orders.
            </p>
          </div>
          <AssistantShowcase C={C} />
        </div>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section className="py-20 px-5"
        style={{ background: "#0d0d0d", borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-3xl mx-auto">
          <SectionLabel text="Who it's for" color={C.muted} />
          <h2 className="text-base sm:text-lg font-bold mb-5" style={{ color: C.text }}>
            Built for traders who manage positions, not just open them.
          </h2>
          <p className="text-sm leading-relaxed mb-8" style={{ color: C.muted }}>
            If you sell premium, run spreads, or manage a wheel — you already think in probabilities.
            MarkovLab gives you the infrastructure to act on that thinking across the full trade lifecycle.
            From regime-aware entry to rules-based exit. One platform. No improvisation.
          </p>
          <button onClick={cta}
            className="h-10 px-6 text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-opacity hover:opacity-85"
            style={{ background: C.accent, color: "#0a0a0a" }}
            data-testid="bottom-cta">
            Start 15-day Demo <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>
    </PublicPage>
  );
}
